import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import type { MentionDescription, MentionsResult } from '@atlaskit/mention/types';

import { IdentityUserAri } from '@atlassian/ari/identity/user';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';

import type {
	queryAgentMentionsByNameQuery$data,
	queryAgentMentionsByNameQuery$variables,
} from './__generated__/queryAgentMentionsByNameQuery.graphql.ts';
import type { queryAgentMentionsFragment$data } from './__generated__/queryAgentMentionsFragment.graphql.ts';
import type {
	queryAgentMentionsForInitialStateQuery$data,
	queryAgentMentionsForInitialStateQuery$variables,
} from './__generated__/queryAgentMentionsForInitialStateQuery.graphql.ts';

const PRODUCT = 'confluence';
export async function queryAgentMentions(
	query: string,
	maxResults: number,
): Promise<MentionsResult> {
	let mentions: MentionsResult['mentions'];

	try {
		mentions = query
			? await queryAgentMentionsByName(query, maxResults)
			: await queryAgentMentionsForInitialState(maxResults);
	} catch (error) {
		getMonitoringClient().submitError(error, { attribution: Attribution.COMMENTS });
		markErrorAsHandled(error);

		throw error;
	}

	return { query, mentions };
}

async function queryAgentMentionsByName(
	query: string,
	maxResults: number,
): Promise<MentionDescription[]> {
	const { cloudId } = await getSessionData();
	const { data, error } = await queryInterop<
		queryAgentMentionsByNameQuery$data,
		queryAgentMentionsByNameQuery$variables
	>({
		query: queryAgentMentionsByNameQuery,
		variables: {
			cloudId,
			first: maxResults,
			input: { name: query },
			product: PRODUCT,
		},
		interopApolloAggClient: true,
	});

	if (error) {
		throw error;
	}

	return convertToMentions(data?.agentStudio_getAgents?.edges);
}

async function queryAgentMentionsForInitialState(
	maxResults: number,
): Promise<MentionDescription[]> {
	const { cloudId } = await getSessionData();
	const { data, error } = await queryInterop<
		queryAgentMentionsForInitialStateQuery$data,
		queryAgentMentionsForInitialStateQuery$variables
	>({
		query: queryAgentMentionsForInitialStateQuery,
		variables: {
			cloudId,
			first: maxResults,
			product: PRODUCT,
		},
		interopApolloAggClient: true,
	});

	if (error) {
		throw error;
	}

	const mentions = [
		...convertToMentions(data?.favoriteAgents?.edges),
		...convertToMentions(data?.verifiedAgents?.edges),
		...convertToMentions(data?.allAgents?.edges),
	];

	// Only return the first distinct maxResults:
	const returnedMentionIds = new Set<string>();
	return mentions.filter((mention) => {
		if (returnedMentionIds.size >= maxResults || returnedMentionIds.has(mention.id)) {
			return false;
		}

		returnedMentionIds.add(mention.id);
		return true;
	});
}

function convertToMentions(
	edges?: NonNullable<queryAgentMentionsByNameQuery$data['agentStudio_getAgents']>['edges'],
): MentionDescription[] {
	return (
		edges
			?.map((edge) => edge.node)
			.filter((agent): agent is Parameters<typeof convertToMentionDescription>[0] =>
				Boolean(agent?.identityAccountId && agent?.name),
			)
			.map(convertToMentionDescription) ?? []
	);
}

function convertToMentionDescription(agent: AgentMentionWithRequiredFields): MentionDescription {
	return {
		appType: 'agent',
		avatarUrl: agent.identityAccount?.picture || undefined,
		id: normalizeIdentityAccountIdForMention(agent.identityAccountId),
		mentionName: agent.name,
		name: agent.name,
		userType: 'APP',
	};
}

export function normalizeIdentityAccountIdForMention(identityAccountId: string): string {
	try {
		return IdentityUserAri.parse(identityAccountId).userId;
	} catch {
		return identityAccountId;
	}
}

type AgentMentionWithRequiredFields = queryAgentMentionsFragment$data & {
	readonly identityAccountId: string;
	readonly name: string;
};

export const queryAgentMentionsFragment: GraphQLTaggedNode = graphql`
	fragment queryAgentMentionsFragment on AgentStudioAssistant {
		id
		identityAccount {
			picture
		}
		identityAccountId
		name
	}
`;

const queryAgentMentionsByNameQuery = graphql`
	query queryAgentMentionsByNameQuery(
		$cloudId: String!
		$first: Int
		$input: AgentStudioAgentQueryInput
		$product: String
	) {
		agentStudio_getAgents(cloudId: $cloudId, first: $first, input: $input, product: $product)
			@optIn(to: "AgentStudio") {
			edges {
				node {
					...queryAgentMentionsFragment @relay(mask: false)
				}
			}
		}
	}
`;

const queryAgentMentionsForInitialStateQuery = graphql`
	query queryAgentMentionsForInitialStateQuery($cloudId: String!, $first: Int, $product: String) {
		favoriteAgents: agentStudio_getAgents(
			cloudId: $cloudId
			first: $first
			input: { onlyFavouriteAgents: true }
			product: $product
		) @optIn(to: "AgentStudio") {
			edges {
				node {
					...queryAgentMentionsFragment @relay(mask: false)
				}
			}
		}
		verifiedAgents: agentStudio_getAgents(
			cloudId: $cloudId
			first: $first
			input: { onlyVerifiedAgents: true }
			product: $product
		) @optIn(to: "AgentStudio") {
			edges {
				node {
					...queryAgentMentionsFragment @relay(mask: false)
				}
			}
		}
		allAgents: agentStudio_getAgents(cloudId: $cloudId, first: $first, product: $product)
			@optIn(to: "AgentStudio") {
			edges {
				node {
					...queryAgentMentionsFragment @relay(mask: false)
				}
			}
		}
	}
`;
